import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  UserPlus, Mail, Lock, User, Loader2, CheckCircle2, 
  AlertCircle, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Brand } from '@/components/brand/Brand';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (token) {
      fetchInvitation();
    } else {
      setError('No invitation token provided');
      setLoading(false);
    }
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const response = await axios.get(`${API}/api/team/invitation/${token}`);
      setInvitation(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/team/accept-invitation`, null, {
        params: {
          token,
          first_name: formData.first_name,
          last_name: formData.last_name,
          password: formData.password
        }
      });

      // Auto-login: stash the freshly-minted tokens so the AuthGate
      // recognizes the new user when /dashboard mounts. Without this,
      // the user lands on /dashboard with no JWT and gets bounced to
      // /login. Mirrors the localStorage shape set by LoginPage.
      const data = response.data || {};
      if (data.access_token) {
        localStorage.setItem('accessToken', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      if (data.user_id) localStorage.setItem('userId', data.user_id);
      if (data.company_id) localStorage.setItem('companyId', data.company_id);
      // Access-token TTL is 15 minutes — match LoginPage's stash.
      localStorage.setItem(
        'tokenExpiresAt',
        String(Date.now() + 14 * 60 * 1000)
      );

      toast.success('Account created successfully! Welcome to KEY-MADE.');

      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#0D0D0D] border-[#1A1A1A]" data-testid="join-invalid-card">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#E5E2DD] mb-2">
              This invitation link is no longer valid
            </h2>
            <p className="text-[#8A8A8A] mb-2">{error}</p>
            <p className="text-[#8A8A8A] mb-6 text-sm">
              Invitation links are single-use and expire 7 days after they&apos;re sent.
              Please ask your administrator to send you a new one.
            </p>
            <p className="text-[#6B6B6B] text-xs">
              Do <strong>not</strong> sign up — that would create a separate account
              outside your team&apos;s workspace.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto flex items-center justify-center mb-4">
            <Brand size="lg" variant="mark" testId="join-logo" />
          </div>
          <h1 className="text-2xl font-bold text-[#E5E2DD]">Join KEY-MADE</h1>
          <p className="text-[#8A8A8A] mt-1">Design-Build Workflow Platform</p>
        </div>

        <Card className="bg-[#0D0D0D] border-[#1A1A1A]">
          <CardHeader>
            <CardTitle className="text-[#E5E2DD] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#BC5D41]" />
              Accept Invitation
            </CardTitle>
            <CardDescription className="text-[#8A8A8A]">
              {invitation?.invited_by} has invited you to join as a <strong className="text-[#E5E2DD]">{invitation?.role}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitation?.personal_message && (
              <div className="mb-6 p-3 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
                <p className="text-sm text-[#8A8A8A] italic">"{invitation.personal_message}"</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#E5E2DD]">First Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                      className="pl-9 bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E2DD]">Last Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                      className="pl-9 bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[#E5E2DD]">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                  <Input
                    value={invitation?.email || ''}
                    disabled
                    className="pl-9 bg-[#0D0D0D] border-[#2A2A2A] text-[#8A8A8A]"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#E5E2DD]">Create Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    minLength={8}
                    className="pl-9 bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]"
                    placeholder="At least 8 characters"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#E5E2DD]">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                  <Input
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    required
                    className="pl-9 bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#BC5D41] hover:bg-[#70170F] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Create Account & Join
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-xs text-[#6B6B6B] mt-4">
              By joining, you agree to the KEY-MADE Terms of Service and Privacy Policy.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
